import { useEffect, useState } from "react";
import { Box, Button, Paper, Typography } from "@mui/material";
import {
  ChevronLeft as BackIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from "@mui/icons-material";
import { useFormContext, useWatch } from "react-hook-form";
import { flattenItems } from "../../MDDForm/helper.js";
import {
  borderRadius,
  colorsFoundation,
  dimensions,
  typographyTokens,
} from "@accredo/fe-shared/theme/index.js";
import { selectedCoverageKeys } from "../FormDefinitionRenderer/validateCoverageSteps.js";
import CoverageSparteSelect from "./CoverageSparteSelect.jsx";
import {
  useFormActions,
  useFormCoverage,
  useFormSubmitHandler,
} from "../FormDefinitionRenderer/contexts.js";
// eslint-disable-next-line import/no-cycle
import FormDefinitionRenderer from "../FormDefinitionRenderer/FormDefinitionRenderer.jsx";

function stepFieldNames(step) {
  return flattenItems(step.children)
    .map((f) => f.name)
    .filter(Boolean);
}

export default function StepperControl({ item }) {
  const {
    formState: { errors, isDirty, isValid },
    reset,
    trigger,
  } = useFormContext();
  const actions = useFormActions();
  const makeSubmitHandler = useFormSubmitHandler();
  const [requestedStep, setRequestedStep] = useState(0);
  const coverage = useFormCoverage();
  const coverage_types = useWatch({ name: "_coverage_types" });

  // A step marked with a coverage type is only shown while that type is selected.
  const selected_keys = selectedCoverageKeys(
    coverage_types,
    coverage.objektSparten
  );
  const visible_indexes = item.steps
    .map((_, index) => index)
    .filter(
      (index) =>
        !coverage.enabled ||
        !item.steps[index].coverage_type ||
        selected_keys.includes(item.steps[index].coverage_type)
    );
  const earlier_visible = visible_indexes.filter((i) => i <= requestedStep);
  const activeStep = visible_indexes.includes(requestedStep)
    ? requestedStep
    : (earlier_visible.at(-1) ?? visible_indexes[0] ?? requestedStep);
  const previous_step = visible_indexes.filter((i) => i < activeStep).at(-1);
  const next_step = visible_indexes.find((i) => i > activeStep);

  useEffect(() => {
    if (activeStep !== requestedStep) setRequestedStep(activeStep);
  }, [activeStep, requestedStep]);

  const getErrorCount = (step) =>
    stepFieldNames(step).filter((name) => !!errors[name]).length;

  const isStepComplete = (stepIndex) =>
    stepIndex < activeStep && getErrorCount(item.steps[stepIndex]) === 0;

  const getStepIcon = (stepIndex) => {
    const step = item.steps[stepIndex];
    const error_count = getErrorCount(step);
    const is_complete = isStepComplete(stepIndex);
    const is_active = stepIndex === activeStep;

    if (error_count > 0 && stepIndex <= activeStep) {
      return (
        <ErrorIcon
          sx={{ color: colorsFoundation.key.blue[700], fontSize: 24 }}
        />
      );
    }
    if (is_complete) {
      return (
        <CheckCircleIcon
          sx={{ color: colorsFoundation.status.positive[600], fontSize: 24 }}
        />
      );
    }
    return (
      <RadioButtonUncheckedIcon
        sx={{
          color: is_active
            ? colorsFoundation.key.blue[700]
            : colorsFoundation.neutral[300],
          fontSize: 24,
        }}
      />
    );
  };

  const isSubmitEnabled = (action) => {
    if (action.enabled === "always") return true;
    if (action.enabled === "valid") return isValid;
    if (typeof action.enabled === "function")
      return action.enabled({ isDirty, isValid, coverage: coverage.validity });
    return isDirty && isValid;
  };

  const current_step = item.steps[activeStep];
  if (!current_step) return null;

  return (
    <Box sx={{ display: "flex", gap: 3, width: "100%", minHeight: "600px" }}>
      {/* Left: current step content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Paper elevation={0}>
          <Typography
            variant="h5"
            sx={{
              mb: 2,
              fontWeight: typographyTokens.weight.semibold,
              fontSize: typographyTokens.size["2xl"],
            }}
          >
            {current_step.label}
          </Typography>

          {current_step.description && (
            <Typography
              variant="body2"
              sx={{
                fontSize: typographyTokens.size.base,
                marginY: dimensions["3xl"],
              }}
            >
              {current_step.description}
            </Typography>
          )}

          {coverage.enabled && current_step.coverage_type && (
            <CoverageSparteSelect coverageType={current_step.coverage_type} />
          )}

          <FormDefinitionRenderer fields={current_step.children} />

          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 4,
              justifyContent: "space-between",
            }}
          >
            {previous_step === undefined ? (
              <div />
            ) : (
              <Button
                startIcon={<BackIcon />}
                variant="text"
                onClick={() => setRequestedStep(previous_step)}
                sx={{
                  textTransform: "none",
                  color: colorsFoundation.neutral[700],
                }}
              >
                Zurück
              </Button>
            )}
            {next_step !== undefined ? (
              <Button
                variant="contained"
                onClick={async () => {
                  await trigger(stepFieldNames(current_step));
                  setRequestedStep(next_step);
                }}
                sx={{ textTransform: "none" }}
              >
                Weiter
              </Button>
            ) : (
              <div />
            )}
          </Box>
        </Paper>
      </Box>

      {/* Right: sticky progress sidebar */}
      <Box
        sx={{
          width: 280,
          flexShrink: 0,
          alignSelf: "flex-start",
          position: "sticky",
          top: 84,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            backgroundColor: colorsFoundation.neutral[50],
            borderRadius: borderRadius.sm,
            p: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            {visible_indexes.map((index) => {
              const step = item.steps[index];
              const error_count = getErrorCount(step);
              const is_active = index === activeStep;
              const is_complete = isStepComplete(index);
              const has_errors = error_count > 0 && index <= activeStep;

              return (
                <Box
                  key={step.label}
                  onClick={async () => {
                    if (index > activeStep) {
                      const names = visible_indexes
                        .filter((i) => i >= activeStep && i < index)
                        .flatMap((i) => stepFieldNames(item.steps[i]));
                      await trigger(names);
                    }
                    setRequestedStep(index);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    cursor: "pointer",
                    p: 1.5,
                    borderRadius: borderRadius.sm,
                    backgroundColor: is_active
                      ? colorsFoundation.neutral[100]
                      : "transparent",
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor: colorsFoundation.neutral[100],
                    },
                  }}
                >
                  <Box sx={{ flexShrink: 0, mt: 0.25 }}>
                    {getStepIcon(index)}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, mt: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: typographyTokens.weight.semibold,
                        color: colorsFoundation.key.blue[700],
                        wordBreak: "break-word",
                      }}
                    >
                      {step.label}
                    </Typography>
                    {has_errors && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: colorsFoundation.status.critical[700],
                          display: "block",
                          mt: 0.5,
                        }}
                      >
                        {error_count} offen
                      </Typography>
                    )}
                    {is_complete && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: colorsFoundation.status.positive[600],
                          display: "block",
                          mt: 0.5,
                        }}
                      >
                        Abgeschlossen
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {actions.length > 0 && (
            <Box
              sx={{
                mt: 3,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                pt: 2,
                borderTop: `1px solid ${colorsFoundation.neutral[200]}`,
              }}
            >
              {actions.map((action, i) => {
                if (action.type === "cancel") {
                  return (
                    <Button
                      key={i}
                      fullWidth
                      sx={{ textTransform: "none" }}
                      color="inherit"
                      onClick={() => reset()}
                    >
                      {action.label ?? "Abbrechen"}
                    </Button>
                  );
                }
                if (action.type === "submit") {
                  return (
                    <Button
                      key={i}
                      type="button"
                      fullWidth
                      variant="contained"
                      color={action.color ?? "primary"}
                      disabled={!isSubmitEnabled(action)}
                      sx={{ textTransform: "none" }}
                      onClick={makeSubmitHandler(action)}
                    >
                      {action.label ?? "Speichern"}
                    </Button>
                  );
                }
                return null;
              })}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
